/**
 * money.ts
 * Money value object + currency vocabulary — KC-9.1 (add-cuentas-pagar,
 * design.md D5/D6).
 *
 * Lives in contracts-kernel, NOT inside cuentas-pagar: if it were born only
 * for AP, `compras` would keep its own ad-hoc `currency`/`exchangeRate` model
 * and two currency shapes would coexist (design.md D6, audit Fase 2.4).
 * `compras` is NOT retrofitted by this change — the kernel home is
 * established now, migrating existing consumers is a later, independent
 * change.
 *
 * Money WRAPS the D5 numeric model (`number` + roundToCents), it does not
 * replace it — internally it holds a single cents-rounded `number`, so there
 * is exactly one numeric model in the repo, not two. It performs NO FX
 * conversion: converting with an explicit exchange rate is the caller's
 * concern (e.g. cuentas-pagar-ledger AP-4.2 / validateImputacion).
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Currency vocabulary
// ---------------------------------------------------------------------------

export const CURRENCY_CODES = ['ARS', 'USD'] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];
export const CurrencyCodeSchema = z.enum(CURRENCY_CODES);

/** WIRE shape (Zod). `amount` is a decimal STRING — numeric(18,2) round-trips as text. */
export const MoneySchema = z.object({
  amount: z.string().regex(/^-?\d{1,16}(\.\d{1,2})?$/, 'Monto con hasta 2 decimales.'),
  currency: CurrencyCodeSchema,
});

/** The wire/DTO type. The DOMAIN type is the `Money` class below — do not conflate. */
export type MoneyWire = z.infer<typeof MoneySchema>;

// ---------------------------------------------------------------------------
// Domain-side helpers (D5) — every arithmetic result passes through roundToCents.
// ---------------------------------------------------------------------------

/** Rounds a number to 2 decimal places (cents). IEEE-754 is exact for integers
 * up to 2^53; rounding after each step keeps the accumulated error at zero
 * for realistic magnitudes (design.md D5). */
export function roundToCents(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Parses a decimal string (DB/wire boundary) into a cents-rounded number. */
export function toCentsNumber(amount: string): number {
  return roundToCents(Number(amount));
}

/** Formats a number as a fixed 2-decimal string for the DB/wire boundary. */
export function toAmountString(n: number): string {
  return roundToCents(n).toFixed(2);
}

// ---------------------------------------------------------------------------
// CurrencyMismatchError — typed, catchable, `instanceof`-narrowable (KC-9.1).
// NEVER a bare Error.
// ---------------------------------------------------------------------------

export class CurrencyMismatchError extends Error {
  readonly kind = 'currency_mismatch' as const;

  constructor(
    readonly left: CurrencyCode,
    readonly right: CurrencyCode,
  ) {
    super(`No se pueden operar montos en monedas distintas (${left} vs ${right}).`);
    this.name = 'CurrencyMismatchError';
  }
}

// ---------------------------------------------------------------------------
// Money — immutable value object (KC-9.1).
// ---------------------------------------------------------------------------

/**
 * IMMUTABLE value object. Every operation returns a NEW Money; no setter, no
 * mutable field. Internally a cents-rounded `number` (D5) — one numeric
 * model in the repo, not two. NO FX conversion lives here.
 */
export class Money {
  private constructor(
    readonly amount: number, // already roundToCents'd
    readonly currency: CurrencyCode,
  ) {
    Object.freeze(this);
  }

  /** Accepts the DB/wire decimal string or a number; rounds to cents once, here. */
  static of(amount: string | number, currency: CurrencyCode): Money {
    const rounded = typeof amount === 'string' ? toCentsNumber(amount) : roundToCents(amount);
    return new Money(rounded, currency);
  }

  static fromWire(w: MoneyWire): Money {
    return Money.of(w.amount, w.currency);
  }

  private assertSameCurrency(other: Money): void {
    if (other.currency !== this.currency) {
      throw new CurrencyMismatchError(this.currency, other.currency);
    }
  }

  /** @throws CurrencyMismatchError when `other.currency !== this.currency`. */
  add(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(roundToCents(this.amount + other.amount), this.currency);
  }

  /** @throws CurrencyMismatchError when `other.currency !== this.currency`. */
  subtract(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(roundToCents(this.amount - other.amount), this.currency);
  }

  /** @throws CurrencyMismatchError when `other.currency !== this.currency` (D6). */
  equals(other: Money): boolean {
    this.assertSameCurrency(other);
    return this.amount === other.amount;
  }

  /** Non-throwing predicate — the escape hatch for heterogeneous collections. */
  isSameCurrency(other: Money): boolean {
    return this.currency === other.currency;
  }

  negate(): Money {
    return new Money(roundToCents(-this.amount), this.currency);
  }

  isZero(): boolean {
    return this.amount === 0;
  }

  /** Decimal string for `numeric(18,2)` / JSON. */
  toWire(): MoneyWire {
    return { amount: toAmountString(this.amount), currency: this.currency };
  }
}
